import React, { useState, useEffect } from "react";
import { Avatar, Skeleton, SkeletonItem, Tooltip } from "@fluentui/react-components";
import {
  BriefcaseIcon,
  CurrencyIcon,
  FolderIcon,
  HandShakeIcon,
  PersonIcon,
  TimerIcon,
} from "../../../modules/default-svg";
import HoverCard from "../../../components/hoverCard/HoverCard";
import {
  getAccountCurrency,
  displayDecimalFormatted,
  weeksBetweenDates
} from "../../../utils/systemSettings";
import { useHistory } from "react-router-dom";
import useNavigation from "../../../utils/useNavigation";
import ResourceDetails from "../../resourcePlanner/resourceDetails/ResourceDetails";
import Modal from "../../../components/controls/modal/Modal";
import { apiInstance, routes } from "../../../helpers/API";
import { useDispatch } from "react-redux";
import { generalActions } from "../../../store/actions";
import SafeHtml from "../../../components/safeHtml/SafeHtml";
import { CalendarClock } from "lucide-react";
import projectService from "../../../store/services/projects.service";
import TeamManagement from "../../../components/teamManagement/TeamManagement";
import { getLabelByType } from "../../../helpers/utils";

const ProjectDetails = (props) => {
  const { projectData } = props;
  const { navigateTo } = useNavigation();
  const displatch = useDispatch();
  const settings = JSON.parse(localStorage.getItem("settings"));
  const [isResourceDetailsLoading, setIsResourceDetailsLoading] = useState(false);
  const [userDetails, setUserDetails] = useState(false);
  const [resourceTasks, setResourceTasks] = useState([]);
  const [setting] = useState(settings);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [resources, setResources] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("byScope");

  const handleRedirection = (type, id, name) => {
    switch (type) {
      case "Client": {
        // Use navigation hook for direct navigation to client
        navigateTo("Clients", id, projectData.client.name);
        break;
      }
      case "Portfolio": {
        // Use navigation hook for direct navigation to portfolio
        navigateTo("Portfolios", id, projectData.portfolio.name);
        break;
      }
      case "Program": {
        // Use navigation hook for direct navigation to program
        navigateTo("Programs", id, projectData.program.name);
        break;
      }
      case "Scope": {
        navigateTo("ScopeItems", id, name);
        break;
      }
      default:
        return "";
    }
  };

  const getResourceContribution = async (id) => {
    try {
      setIsResourceDetailsLoading(true);
      setResourceTasks([]);
      const response = await apiInstance.get(
        `${routes.getContribution.replace(":resourceId", id)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage
              ?.getItem("accessToken")
              ?.replace(/"/g, "")}`,
          },
          params: {
            ProjectId: projectData.id,
          },
        }
      );
      const {
        data: { resource, allocatedCapacity, totalCapacity, tasks },
      } = response;
      setIsResourceDetailsLoading(false);
      setUserDetails({ ...resource, allocatedCapacity, totalCapacity });
      setResourceTasks(tasks.length > 0 ? tasks : []);
    } catch (error) {
      setIsResourceDetailsLoading(false);
      return displatch(
        generalActions.showToast({
          title: error.message,
          type: "error",
        })
      );
    }
  };

  const fetchResources = () => {
    setIsLoading(true);
    projectService.getTeam(projectData.id)
      .then((response) => {
        setResources(response);
        setIsLoading(false);
      }
      )
      .catch((error) => { })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (projectData?.id) {
      fetchResources();
    }
  }, [projectData]);

  const handleTeamUpdate = () => {
    fetchResources();
  };

  const handleCancel = () => setShowDetailModal(false);
  const handleOpen = (user) => {
    setUserDetails(user);
    setShowDetailModal(true);
    getResourceContribution(user.id);
  };

  return (
    <div className="DetailsTabScroll">
      <div className="row">
        {projectData && (
          <>
            <div className="col-lg-8">
              <div className="CustomCard p-4">
                {projectData.refId && (
                  <Tooltip content="Reference ID" relationship="label">
                    <p className="detailBadge mr-2">{projectData.refId}</p>
                  </Tooltip>  
                )}
                <Tooltip content="Project Type" relationship="label">
                <p className={`detailBadge project-type-${projectData.type}`}>{getLabelByType("projectType", projectData.type)}</p>
                </Tooltip>
                <h5 className="mb-12">{projectData.name}</h5>
                <div className="detailsInfoWrap">
                  <div className="col-3">
                    <div className="d-flex align-items-centers">
                      <span className="me-2 d-inline-flex">
                        <PersonIcon color="#342C82" />
                      </span>
                      <p className="text-md-400 grey46Color">Client</p>
                    </div>
                  </div>
                  <div className="col-9">
                    {projectData && (
                      <p
                        className={`text-md-400 grey10Color ${projectData.client && "entityName"
                          }`}
                        onClick={() =>
                          projectData.client
                            ? handleRedirection("Client", projectData.client.id)
                            : null
                        }
                      >
                        {projectData.client ? projectData.client.name : ""}
                      </p>
                    )}
                  </div>
                </div>
                <div className="detailsInfoWrap">
                  <div className="col-3">
                    <div className="d-flex align-items-center">
                      <span className="me-2 d-inline-flex">
                        <BriefcaseIcon color="#342C82" />
                      </span>
                      <p className="text-md-400 grey46Color">Portfolio</p>
                    </div>
                  </div>
                  <div className="col-9">
                    <p
                      className={`text-md-400 grey10Color ${projectData.portfolio && "entityName"
                        }`}
                      onClick={() =>
                        projectData.portfolio
                          ? handleRedirection(
                            "Portfolio",
                            projectData.portfolio.id
                          )
                          : null
                      }
                    >
                      {projectData.portfolio
                        ? projectData.portfolio.name
                        : "--"}
                    </p>
                  </div>
                </div>
                <div className="detailsInfoWrap">
                  <div className="col-3">
                    <div className="d-flex align-items-center">
                      <span className="me-2 d-inline-flex">
                        <FolderIcon color="#342C82" />
                      </span>
                      <p className="text-md-400 grey46Color">Program</p>
                    </div>
                  </div>
                  <div className="col-9">
                    <p
                      className={`text-md-400 grey10Color ${projectData.program && "entityName"
                        }`}
                      onClick={() =>
                        projectData.program
                          ? handleRedirection("Program", projectData.program.id)
                          : null
                      }
                    >
                      {projectData.program ? projectData.program.name : "--"}
                    </p>
                  </div>
                </div>
                <div className="detailsInfoWrap">
                  <div className="col-3">
                    <div className="d-flex align-items-center">
                      <span className="me-2 d-inline-flex">
                        <PersonIcon color="#342C82" />
                      </span>
                      <p className="text-md-400 grey46Color">Sponsor</p>
                    </div>
                  </div>
                  <div className="col-9">
                    <p className="text-md-400 grey10Color">
                      {projectData.sponsor
                        ? projectData.sponsor.name || projectData.sponsor.email || projectData.sponsor.phone
                        : "--"}
                    </p>
                  </div>
                </div>
                <div className="detailsInfoWrap">
                  <div className="col-3">
                    <div className="d-flex align-items-center">
                      <span className="me-2 d-inline-flex">
                        <PersonIcon color="#342C82" />
                      </span>
                      <p className="text-md-400 grey46Color">Executive</p>
                    </div>
                  </div>
                  <div className="col-9">
                    <p className="text-md-400 grey10Color">
                      {projectData.executive
                        ? projectData.executive.name || projectData.executive.email || projectData.executive.phone
                        : "--"}
                    </p>
                  </div>
                </div>
                <div className="detailsInfoWrap">
                  <div className="col-3">
                    <div className="d-flex align-items-center">
                      <span className="me-2 d-inline-flex">
                        <HandShakeIcon color="#342C82" />
                      </span>
                      <p className="text-md-400 grey46Color">Stakeholders</p>
                    </div>
                  </div>
                  <div className="col-9">
                    <p className="text-md-400 grey10Color">
                      {projectData.stakeholders
                        ? projectData.stakeholders.map((contact) => contact.name || contact.email || contact.phone).join(", ")
                        : "--"}
                    </p>
                  </div>
                </div>
                <div className="detailsInfoWrap">
                  <div className="col-3">
                    <div className="d-flex align-items-center">
                      <span className="me-2 d-inline-flex">
                        <CalendarClock size={20} strokeWidth={1} color="#342C82" />
                      </span>
                      <p className="text-md-400 grey46Color">Duration</p>
                    </div>
                  </div>
                  <div className="col-9">
                    <p className="text-md-400 grey10Color">
                      {` ${projectData.startDate && (projectData.revisedEndDate || projectData.endDate)
                        ? `${weeksBetweenDates(projectData.startDate, projectData.revisedEndDate || projectData.endDate)}`
                        : ""}`}
                    </p>
                  </div>
                </div>
                <div className="detailsInfoWrap">
                  <div className="col-3">
                    <div className="d-flex align-items-center">
                      <span className="me-2 d-inline-flex">
                        <CurrencyIcon color="#342C82" />
                      </span>
                      <p className="text-md-400 grey46Color">Budget</p>
                    </div>
                  </div>
                  <div className="col-9">
                    <p className="text-md-400 grey10Color">
                      {projectData.budget
                        ? `${getAccountCurrency(
                          setting
                        )}${displayDecimalFormatted(
                          setting,
                          projectData.budget
                        )}`
                        : ""}
                    </p>
                  </div>
                </div>
                {projectData.description &&
                  <div style={{ borderBottom: "1px solid #ebebeb" }}>
                    <h6 className="font-weight-600 grey46Color my-3"> Description </h6>
                    <p className="text-md-400 BaseMainColor mb-12" style={{ whiteSpace: "pre-line" }}>
                      <SafeHtml html={projectData.description} />
                    </p>
                  </div>
                }
              </div>
            </div>

            <div className="col-lg-4">
              <div className="CustomCard p-4">
                <div className="d-flex justify-content-between align-items-center mb-20">
                  <div className="d-flex align-items-center gap-2">
                    <h4 className="mb-0">Team</h4>
                    
                    {/* Tabs */}
                    <div className="resourcesTabToggleContainer">
                      <button
                        className={`resourcesTabToggleButton ${activeTab === "byScope" ? "active" : ""}`}
                        onClick={() => setActiveTab("byScope")}
                      >
                        By Scope
                      </button>
                      <button
                        className={`resourcesTabToggleButton ${activeTab === "all" ? "active" : ""}`}
                        onClick={() => setActiveTab("all")}
                      >
                        All
                      </button>
                    </div>
                  </div>

                  <div>
                    {isLoading ? (
                      <Skeleton>
                        <SkeletonItem style={{ width: "100px", height: "30px", borderRadius: "20px" }} />
                      </Skeleton>
                    ) : (
                      <TeamManagement
                        entityType={"project"}
                        entityId={projectData?.id}
                        parentId={projectData?.client?.id}
                        onTeamUpdate={handleTeamUpdate}
                        initialTeamUsers={resources ? resources.resources : []}
                      />
                    )}
                  </div>
                </div>

                {isLoading ? (
                  [...Array(5)].map((_, index) => (
                    <div key={index} className="d-flex align-items-center CustomCardWrap pl-2 mb-3">
                      <Skeleton>
                        <SkeletonItem size={32} shape="circle" />
                      </Skeleton>
                      <div className="ms-12 flex-grow-1">
                        <Skeleton>
                          <SkeletonItem style={{ width: '60%', height: '20px' }} />
                        </Skeleton>
                      </div>
                    </div>
                  ))
                ) : (
                  <>
                    {activeTab === "byScope" ? (
                      // By Scope View
                      resources && resources.resourcesByScope ? (
                        [...resources.resourcesByScope].sort((a, b) => (a.scope.name || '').localeCompare(b.scope.name || '')).map((item) => (
                          <div key={item.scope.id} className="bg-[#f9f8ff] rounded-lg p-3 mb-3">
                            <p
                              className="text-[13px] text-[#342c82] font-figtree font-semibold mb-2 hover:font-bold cursor-pointer"
                              onClick={() => handleRedirection("Scope", item.scope.id, item.scope.name)}
                            >
                              {item.scope.name}
                            </p>
                            {[...item.users].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((user) => (
                              <div
                                className="d-flex align-items-center CustomCardWrap pl-2"
                                key={user.id}
                              >
                                <HoverCard
                                  HoverCardTrigger={
                                    <Avatar
                                      color="colorful"
                                      size={28}
                                      name={user.name}
                                      image={{ src: user.image ? user.image : "" }}
                                    />
                                  }
                                  AvatarId={user.id}
                                  AvatarName={user.name}
                                  AvatarImage={user.image}
                                  AvatarDesignation={user.title}
                                  AvatarEmail={user.email}
                                  AvatarPhone={user.phoneNumber}
                                  AvatarIsExternal={user.isExternal}
                                />
                                {/* <div className="ms-12 hover:font-bold hover:underline cursor-pointer" onClick={() => handleOpen(user)}>
                                  <p className="mb-1 textColor3">{user.name}</p>
                                </div> */}
                                <div className="ms-12 resourceNameContainer">
                                  <span className="resourceName">{user.name}</span>
                                  {user?.title && <span className="resourceTitle">{user.title}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          <p className="text-sm">No resources assigned by scope</p>
                        </div>
                      )
                    ) : (
                      // All View
                      resources && resources.resources ? (
                        [...resources.resources].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((user) => (
                          <div
                            className="d-flex align-items-center CustomCardWrap pl-2"
                            key={user.id}
                          >
                            <HoverCard
                              HoverCardTrigger={
                                <Avatar
                                  color="colorful"
                                  size={28}
                                  name={user.name}
                                  image={{ src: user.image ? user.image : "" }}
                                />
                              }
                              AvatarId={user.id}
                              AvatarName={user.name}
                              AvatarImage={user.image}
                              AvatarDesignation={user.title}
                              AvatarEmail={user.email}
                              AvatarPhone={user.phoneNumber}
                              AvatarIsExternal={user.isExternal}
                            />
                            {/* <div className="ms-12 hover:font-bold hover:underline cursor-pointer" onClick={() => handleOpen(user)}>
                              <p className="mb-1 textColor3">{user.name}</p>
                            </div> */}
                            <div className="ms-12 resourceNameContainer">
                                  <span className="resourceName">{user.name}</span>
                                  {user?.title && <span className="resourceTitle">{user.title}</span>}
                                </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          <p className="text-sm">No team members yet</p>
                        </div>
                      )
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {showDetailModal && (
        <Modal
          title="Resource Details"
          footer={false}
          handleCancel={() => handleCancel(false)}
          handleSave={() => handleCancel(false)}
          saveBtnLabel="Save"
          cancelBtnLabel="Cancel"
          isLoading={false}
          size="large"
          calendarModalWrap={true}
          body={
            <ResourceDetails
              isLoading={isResourceDetailsLoading}
              userDetails={userDetails}
              shift={null}
              tasks={resourceTasks}
              isProjects={false}
              isTasks={true}
              defaultTab="tasks"
            />
          }
        />
      )}
    </div>
  );
};
export default ProjectDetails;
